import { useRecoilValue } from 'recoil';
import { BundleCollectionHandle, isBundleCollectionHandle } from '../enums/CollectionHandle.enum';
import { ProductHandle } from '../enums/ProductHandle.enum';
import { ShopifyCollection } from '../models/ShopifyCollection.model';
import { allProductsState } from '../recoil/atoms';
import { isMobileState } from '../recoil/selectors';
import { BundleConfig, bundleConfiguration } from './bundleConfiguration';
import { ProductAssets, useProductAssets } from './useProductAssets';

export interface BundleConfigWithAssets extends BundleConfig {
  title: string;
  productAssets: Partial<Record<ProductHandle, ProductAssets>>;
}

// Force the cache key to be generated via this function via typescript branding
type CacheKey = string & { __cacheKeyBrand: void };
const cacheKey = (collectionHandle: BundleCollectionHandle, isMobile: boolean): CacheKey => `${collectionHandle.replace(/-/g, '_')}-${isMobile}` as CacheKey;
const assetCache: Record<CacheKey, BundleConfigWithAssets> = {};

type BundleConfigLookupType = ShopifyCollection | BundleCollectionHandle;

// FIXME: We don't want this to return undefined
// It shouldn't be called when not in a bundle context
export const useBundleConfig = (collection: BundleConfigLookupType): BundleConfigWithAssets => {
  const allProducts = useRecoilValue(allProductsState);
  const isMobile = useRecoilValue(isMobileState);

  const propsHandle = isBundleCollectionHandle(collection) ? collection : collection.handle;
  const collectionHandle = isBundleCollectionHandle(propsHandle) ? propsHandle : BundleCollectionHandle.SilhouetteBundle;
  const collectionTitle = isBundleCollectionHandle(collection) ? undefined : collection?.title;

  // Even though it normally isn't safe to call hooks in loops, ProductHandle is constant and doesn't change (and frozen via Object.freeze)
  // So the hook will always execute the same number of times in the same order
  const productAssets: Record<ProductHandle, ProductAssets> = Object.values(ProductHandle).reduce(
    // eslint-disable-next-line react-hooks/rules-of-hooks
    (acc, handle) => ({ ...acc, [handle]: useProductAssets(allProducts[handle]) }),
    {} as Record<ProductHandle, ProductAssets>,
  );

  const key = cacheKey(collectionHandle, isMobile);
  if (assetCache[key]) {
    return assetCache[key];
  }

  const assetsFromHandles = (handles: ProductHandle[]) =>
    handles.reduce((acc, curr) => ({ ...acc, [curr]: productAssets[curr] }), {} as Partial<Record<ProductHandle, ProductAssets>>);

  const cache = (assets: Pick<BundleConfigWithAssets, 'productAssets'>): BundleConfigWithAssets => {
    const item = { ...bundleConfiguration[collectionHandle], ...assets, title: collectionTitle ?? bundleConfiguration[collectionHandle].title };
    assetCache[key] = item;
    return item;
  };

  switch (collectionHandle) {
    case BundleCollectionHandle.SilhouetteBundle:
    default:
      return cache({
        productAssets: {
          ...assetsFromHandles([ProductHandle.SilhouetteBra, ProductHandle.SilhouetteBrief]),
        },
      });
  }
};
